import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import './WithdrawalRequest.css';

const WithdrawalRequest = () => {
  const { token } = useAuth();
  const [settings, setSettings] = useState({});
  const [formData, setFormData] = useState({
    amount: '',
    withdrawal_method: 'bank_transfer',
    account_details: {},
    user_remarks: ''
  });
  const [loading, setLoading] = useState(false);
  const [userBalance, setUserBalance] = useState(0);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchSettings();
    fetchUserBalance();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get('/withdrawals/settings', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data.settings);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchUserBalance = async () => {
    try {
      // Authorization header ကို "Bearer token" ပုံစံနဲ့ ပေးပို့ခြင်း
      const response = await axios.get('/app-users/me', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.userInfo && response.data.userInfo.balance !== undefined) {
        setUserBalance(response.data.userInfo.balance);
      } else {
        // Fallback: try to get balance from deposit endpoint
        const depositResponse = await axios.get('/deposit/balance', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUserBalance(depositResponse.data.balance || 0);
      }
    } catch (error) {
      console.error('Error fetching balance:', error);
      setUserBalance(0);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = 'Please enter a valid amount';
    }
    
    const amount = parseFloat(formData.amount);
    const minAmount = parseFloat(settings.min_withdrawal_amount || 10);
    const maxAmount = parseFloat(settings.max_withdrawal_amount || 5000);
    
    if (amount < minAmount) {
      newErrors.amount = `Minimum withdrawal amount is ¥${minAmount}`;
    }
    
    if (amount > maxAmount) {
      newErrors.amount = `Maximum withdrawal amount is ¥${maxAmount}`;
    }
    
    if (amount > userBalance) {
      newErrors.amount = `Insufficient balance. Available: ¥${userBalance}`;
    }
    
    // Validate account details based on method
    switch (formData.withdrawal_method) {
      case 'bank_transfer':
        if (!formData.account_details.bank_name) {
          newErrors.bank_name = 'Bank name is required';
        }
        if (!formData.account_details.account_number) {
          newErrors.account_number = 'Account number is required';
        }
        if (!formData.account_details.account_holder) {
          newErrors.account_holder = 'Account holder name is required';
        }
        break;
      case 'mobile_money':
        if (!formData.account_details.mobile_number) {
          newErrors.mobile_number = 'Mobile number is required';
        }
        if (!formData.account_details.provider) {
          newErrors.provider = 'Provider is required';
        }
        break;
      case 'crypto':
        if (!formData.account_details.crypto_type) {
          newErrors.crypto_type = 'Cryptocurrency type is required';
        }
        if (!formData.account_details.wallet_address) {
          newErrors.wallet_address = 'Wallet address is required';
        }
        break;
      case 'paypal':
        if (!formData.account_details.paypal_email) {
          newErrors.paypal_email = 'PayPal email is required';
        }
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleMethodChange = (method) => {
    setFormData({
      ...formData,
      withdrawal_method: method,
      account_details: {}
    });
    setErrors({}); // Clear errors when method changes
  };

  const handleAccountDetailsChange = (field, value) => {
    setFormData({
      ...formData,
      account_details: {
        ...formData.account_details,
        [field]: value
      }
    });
    
    // Clear specific field error
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: undefined
      });
    }
  };

  const calculateFee = () => {
    const amount = parseFloat(formData.amount) || 0;
    const feePercentage = parseFloat(settings.withdrawal_fee_percentage) || 2.5;
    return (amount * feePercentage) / 100;
  };

  const getNetAmount = () => {
    const amount = parseFloat(formData.amount) || 0;
    const fee = calculateFee();
    return Math.max(0, amount - fee);
  };

  const renderAccountDetailsForm = () => {
    switch (formData.withdrawal_method) {
      case 'bank_transfer':
        return (
          <div className="form-group">
            <div className="form-group">
              <label className="form-label">Bank Name *</label>
              <input
                type="text"
                value={formData.account_details.bank_name || ''}
                onChange={(e) => handleAccountDetailsChange('bank_name', e.target.value)}
                className={`form-input ${errors.bank_name ? 'has-error' : ''}`}
                placeholder="Enter bank name"
              />
              {errors.bank_name && (
                <p className="form-error">{errors.bank_name}</p>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Account Number *</label>
              <input
                type="text"
                value={formData.account_details.account_number || ''}
                onChange={(e) => handleAccountDetailsChange('account_number', e.target.value)}
                className={`form-input ${errors.account_number ? 'has-error' : ''}`}
                placeholder="Enter account number"
              />
              {errors.account_number && (
                <p className="form-error">{errors.account_number}</p>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Account Holder Name *</label>
              <input
                type="text"
                value={formData.account_details.account_holder || ''}
                onChange={(e) => handleAccountDetailsChange('account_holder', e.target.value)}
                className={`form-input ${errors.account_holder ? 'has-error' : ''}`}
                placeholder="Enter account holder name"
              />
              {errors.account_holder && (
                <p className="form-error">{errors.account_holder}</p>
              )}
            </div>
          </div>
        );
      case 'mobile_money':
        return (
          <div className="form-group">
            <div className="form-group">
              <label className="form-label">Mobile Number *</label>
              <input
                type="tel"
                value={formData.account_details.mobile_number || ''}
                onChange={(e) => handleAccountDetailsChange('mobile_number', e.target.value)}
                className={`form-input ${errors.mobile_number ? 'has-error' : ''}`}
                placeholder="Enter mobile number"
              />
              {errors.mobile_number && (
                <p className="form-error">{errors.mobile_number}</p>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Provider *</label>
              <select
                value={formData.account_details.provider || ''}
                onChange={(e) => handleAccountDetailsChange('provider', e.target.value)}
                className={`form-select ${errors.provider ? 'has-error' : ''}`}
              >
                <option value="">Select Provider</option>
                <option value="KBZ Pay">KBZ Pay</option>
                <option value="Wave Money">Wave Money</option>
                <option value="AYA Pay">AYA Pay</option>
                <option value="CB Pay">CB Pay</option>
                <option value="Other">Other</option>
              </select>
              {errors.provider && (
                <p className="form-error">{errors.provider}</p>
              )}
            </div>
          </div>
        );
      case 'crypto':
        return (
          <div className="form-group">
            <div className="form-group">
              <label className="form-label">Cryptocurrency *</label>
              <select
                value={formData.account_details.crypto_type || ''}
                onChange={(e) => handleAccountDetailsChange('crypto_type', e.target.value)}
                className={`form-select ${errors.crypto_type ? 'has-error' : ''}`}
              >
                <option value="">Select Cryptocurrency</option>
                <option value="USDT">USDT (Tether)</option>
                <option value="BTC">Bitcoin (BTC)</option>
                <option value="ETH">Ethereum (ETH)</option>
                <option value="BNB">Binance Coin (BNB)</option>
              </select>
              {errors.crypto_type && (
                <p className="form-error">{errors.crypto_type}</p>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Wallet Address *</label>
              <textarea
                value={formData.account_details.wallet_address || ''}
                onChange={(e) => handleAccountDetailsChange('wallet_address', e.target.value)}
                rows="3"
                className={`form-textarea ${errors.wallet_address ? 'has-error' : ''}`}
                placeholder="Enter wallet address"
              />
              {errors.wallet_address && (
                <p className="form-error">{errors.wallet_address}</p>
              )}
            </div>
          </div>
        );
      case 'paypal':
        return (
          <div className="form-group">
            <label className="form-label">PayPal Email *</label>
            <input
              type="email"
              value={formData.account_details.paypal_email || ''}
              onChange={(e) => handleAccountDetailsChange('paypal_email', e.target.value)}
              className={`form-input ${errors.paypal_email ? 'has-error' : ''}`}
              placeholder="Enter PayPal email"
            />
            {errors.paypal_email && (
              <p className="form-error">{errors.paypal_email}</p>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post('/withdrawals/request', formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Withdrawal request submitted successfully!');
      
      // Reset form
      setFormData({
        amount: '',
        withdrawal_method: 'bank_transfer',
        account_details: {},
        user_remarks: ''
      });
      setErrors({});
      
      // Refresh balance
      fetchUserBalance();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Error submitting withdrawal request';
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (settings.withdrawal_enabled !== 'yes') {
    return (
      <div className="withdrawal-request-container">
        <div className="disabled-warning">
          <div className="disabled-warning-icon">⚠️</div>
          <div className="disabled-warning-text">
            Withdrawals are currently disabled. Please contact support for more information.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="withdrawal-request-container">
      <div className="withdrawal-header">
        <h2 className="withdrawal-title">Request Withdrawal</h2>
        <p className="withdrawal-subtitle">Submit a withdrawal request to transfer funds to your account</p>
      </div>
      
      {/* Balance Info */}
      <div className="balance-card">
        <div className="balance-info">
          <h3 className="balance-label">Available Balance</h3>
          <p className="balance-amount">¥{userBalance.toFixed(0)}</p>
        </div>
        <div className="balance-icon">💰</div>
      </div>
      
      <form onSubmit={handleSubmit}>
        {/* Amount */}
        <div className="form-group">
          <label className="form-label">Withdrawal Amount *</label>
          <div className="amount-input-group">
            <span className="amount-input-prefix">¥</span>
            <input
              type="number"
              step="1"
              min={settings.min_withdrawal_amount}
              max={Math.min(settings.max_withdrawal_amount, userBalance)}
              value={formData.amount}
              onChange={(e) => {
                setFormData({ ...formData, amount: e.target.value });
                if (errors.amount) {
                  setErrors({ ...errors, amount: undefined });
                }
              }}
              className={`form-input amount-input ${errors.amount ? 'has-error' : ''}`}
              placeholder="0"
            />
          </div>
          <div className="form-hint">
            <span>Min: ¥{settings.min_withdrawal_amount}</span>
            <span>Max: ¥{settings.max_withdrawal_amount}</span>
          </div>
          {errors.amount && (
            <p className="form-error">{errors.amount}</p>
          )}
        </div>
        
        {/* Fee Calculation */}
        {formData.amount && parseFloat(formData.amount) > 0 && (
          <div className="transaction-summary">
            <h4 className="summary-title">Transaction Summary</h4>
            <div className="summary-row">
              <span>Withdrawal Amount:</span>
              <span>¥{parseFloat(formData.amount).toFixed(0)}</span>
            </div>
            <div className="summary-row">
              <span>Processing Fee ({settings.withdrawal_fee_percentage}%):</span>
              <span className="summary-amount-negative">-¥{calculateFee().toFixed(0)}</span>
            </div>
            <div className="summary-row-divider"></div>
            <div className="summary-row-total">
              <span>You will receive:</span>
              <span className="summary-amount-positive">¥{getNetAmount().toFixed(0)}</span>
            </div>
          </div>
        )}
        
        {/* Withdrawal Method */}
        <div className="form-group">
          <label className="form-label">Withdrawal Method *</label>
          <div className="method-options">
            {[
              { value: 'bank_transfer', label: 'Bank Transfer', icon: '🏦' },
              { value: 'mobile_money', label: 'Mobile Money', icon: '📱' },
              { value: 'crypto', label: 'Cryptocurrency', icon: '₿' },
              { value: 'paypal', label: 'PayPal', icon: '💳' }
            ].map((method) => (
              <div
                key={method.value}
                onClick={() => handleMethodChange(method.value)}
                className={`method-option ${formData.withdrawal_method === method.value ? 'selected' : ''}`}
              >
                <span className="method-option-icon">{method.icon}</span>
                <span className="method-option-label">{method.label}</span>
              </div>
            ))}
          </div>
        </div>
        
        {/* Account Details */}
        <div className="form-group">
          <h3 className="account-details-title">Account Details</h3>
          <div className="account-details-section">
            {renderAccountDetailsForm()}
          </div>
        </div>
        
        {/* User Remarks */}
        <div className="form-group">
          <label className="form-label">Additional Notes (Optional)</label>
          <textarea
            value={formData.user_remarks}
            onChange={(e) => setFormData({ ...formData, user_remarks: e.target.value })}
            rows="3"
            className="form-textarea"
            placeholder="Any additional information or special instructions..."
          />
        </div>
        
        {/* Processing Time Info */}
        <div className="processing-info">
          <div className="processing-info-icon">ℹ️</div>
          <div>
            <p className="processing-info-text">
              <strong>Processing Time:</strong> {settings.withdrawal_processing_time || '1-3 business days'}
            </p>
            <p className="processing-info-detail">
              Your funds will be held until the withdrawal is processed and approved.
            </p>
          </div>
        </div>
        
        {/* Submit Button */}
        <div className="button-group">
          <button
            type="submit"
            disabled={loading || !formData.amount || getNetAmount() <= 0}
            className="btn btn-primary"
          >
            {loading ? (
              <>
                <span className="loading-spinner"></span>
                Processing...
              </>
            ) : (
              'Submit Withdrawal Request'
            )}
          </button>
          <button
            type="button"
            onClick={() => {
              setFormData({
                amount: '',
                withdrawal_method: 'bank_transfer',
                account_details: {},
                user_remarks: ''
              });
              setErrors({});
            }}
            className="btn btn-secondary"
          >
            Reset
          </button>
        </div>
      </form>
    </div>
  );
};

export default WithdrawalRequest;